import { useCallback } from 'react';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import type { Market } from '@//config/domain-locale-map';
import { useLegacyTracking } from '@//providers/LegacyTrackingProvider';
import { useMarket } from '@//providers/MarketSettingsProvider';
import type { LegacyTrackPaymentOptionClickEventData } from './legacy-types';
import { useLegacyTrackShownInterest } from './useLegacyTrackShownInterest';
import { mapElasticVehicleToLegacyItem } from './utils';

interface UseLegacyTrackPaymentOptionClickVariables {
  vehicle: ElasticVehicle;
  information: LegacyTrackPaymentOptionClickEventData['information'];
}

export const createLegacyTrackPaymentOptionClick = (
  market: Market,
  variables: UseLegacyTrackPaymentOptionClickVariables,
): LegacyTrackPaymentOptionClickEventData => {
  const eventData: LegacyTrackPaymentOptionClickEventData = {
    eventType: 'microConversion',
    conversionType: 'paymentOptionClick',
    item: mapElasticVehicleToLegacyItem(market, variables.vehicle),
    information: variables.information,
    pageType: 'product page',
  };
  return eventData;
};

export const useLegacyTrackPaymentOptionClick = () => {
  const market = useMarket();
  const { track } = useLegacyTracking();
  const { trackLegacyShownInterest } = useLegacyTrackShownInterest();

  const trackLegacyPaymentOptionClick = useCallback(
    (variables: UseLegacyTrackPaymentOptionClickVariables) => {
      trackLegacyShownInterest(variables.vehicle);
      track({
        event: 'microConversion',
        data: createLegacyTrackPaymentOptionClick(market, variables),
      });
    },
    [market, track],
  );

  return {
    trackLegacyPaymentOptionClick,
  };
};
